import { html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { GlobalEvents } from "./domain/global-events.enum.js";

/**
 * Custom element wrapper to manage the events triggered by
 * the buttons and input element.
 *
 * - Dispatches an event when the input changes value.
 * - Listens to click events on the buttons and increments/decrements accordingly.
 * - Respects the min and max on the input.
 *
 * TODO: Move to a more appropriate location.
 */
class ProductQuantity extends LitElement {
	@property({ attribute: "data-is-at-minimum", reflect: true, type: Boolean }) isAtMinimum = false;
	@property({ attribute: "data-is-at-maximum", reflect: true, type: Boolean }) isAtMaximum = false;

	override connectedCallback(): void {
		super.connectedCallback();
		this.addEventListener("click", this.onClick.bind(this));
		this.addEventListener("change", this.onChange.bind(this));

		const input = this.querySelector<HTMLInputElement>('input[type="number"]');
		if (input) {
			this.validateMinMax(input);
			input.dispatchEvent(new Event("change", { bubbles: true }));
		}
	}

	override disconnectedCallback(): void {
		this.removeEventListener("click", this.onClick.bind(this));
		this.removeEventListener("change", this.onChange.bind(this));
	}

	private onClick(event: Event): void {
		const target = event.target as HTMLElement;
		if (target.tagName !== "BUTTON") {
			return;
		}

		const input = this.querySelector<HTMLInputElement>('input[type="number"]');
		if (!input) {
			return;
		}

		if (target.dataset.action === "input-decrement") {
			input.stepDown();
		}

		if (target.dataset.action === "input-increment") {
			input.stepUp();
		}

		input.dispatchEvent(new Event("change", { bubbles: true }));
	}
	private onChange(event: Event): void {
		const target = event.target as HTMLInputElement;
		if (target.tagName !== "INPUT") {
			return;
		}

		this.validateMinMax(target);

		this.dispatchEvent(
			new CustomEvent(GlobalEvents.ProductQuantityChanged, {
				detail: { value: target.valueAsNumber },
				bubbles: true,
				composed: true,
			}),
		);
	}

	private validateMinMax(inputElement: HTMLInputElement): void {
		const min = inputElement.min ? parseInt(inputElement.min, 10) : Number.NEGATIVE_INFINITY;
		const max = inputElement.max ? parseInt(inputElement.max, 10) : Number.POSITIVE_INFINITY;

		this.isAtMinimum = inputElement.valueAsNumber <= min;
		this.isAtMaximum = inputElement.valueAsNumber >= max;
	}

	override render() {
		return html`<slot></slot>`;
	}
}

if (!customElements.get("y-product-quantity")) {
	customElements.define("y-product-quantity", ProductQuantity);
}
